import { ChevronRight, Loader2 } from "lucide-react";
import {
  AppointmentSettings,
  DoctorData,
  DoctorDetails,
  DoctorSettingData,
  Slot,
} from "./types";
import placeholderAvatar from "../../assets/images/default_avatar.jpg";
import React, { useEffect, useState } from "react";
import { AppointmentService } from "../../services/api/appointment.service";
import toast from "react-hot-toast";

interface DoctorCardProps {
  doctor: DoctorSettingData | null;
  isSelected?: boolean;
  isExpanded: boolean;
  selectedTimeSlot: Slot | null;
  onDoctorClick: (doctor: DoctorDetails | null, e: React.MouseEvent) => void;
  onTimeSlotClick: (
    e: React.MouseEvent,
    doctor: DoctorDetails | null,
    schedule: Slot,
  ) => void;
  onBookAppointment?: (e: React.MouseEvent) => void;
  selectedAppointmentSetting: AppointmentSettings | null;
  setSelectedAppointmentSetting: React.Dispatch<
    React.SetStateAction<AppointmentSettings | null>
  >;
  date?: string | null;
}

export function DoctorCard({
  doctor,
  isSelected,
  selectedTimeSlot,
  onDoctorClick,
  onTimeSlotClick,
  onBookAppointment,
  selectedAppointmentSetting,
  setSelectedAppointmentSetting,
  date,
}: DoctorCardProps) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch slots when card is expanded and all requirements are met
  useEffect(() => {
    const fetchSlots = async () => {
      // Fetch if expanded, we have a doctor, a selected setting (clinic), and a date
      if (selectedAppointmentSetting && doctor && date) {
        setLoadingSlots(true);
        try {
          const response = await AppointmentService.getDoctorSlots(
            date,
            selectedAppointmentSetting?.clinicId || "",
            doctor.doctorDetails.userId,
          );
          if (response.success && response.data) {
            const now = new Date();

            const currentMinutes = now.getHours() * 60 + now.getMinutes();

            const selectedDateStr = new Date(date).toDateString();
            const todayStr = new Date().toDateString();

            const isToday = selectedDateStr === todayStr;

            const mappedSlots: Slot[] = response.data
              .filter((s: any) => {
                if (s.status !== "Available") return false;

                if (!isToday) return true; // show all available for future dates

                const [h, m] = s.startTime.split(":").map(Number);
                const slotMinutes = h * 60 + m;

                return slotMinutes > currentMinutes;
              })
              .map((s: any) => ({
                id: s.id,
                start_time: s.startTime?.substring(0, 5),
                end_time: s.endTime?.substring(0, 5),
                slot_date: s.slotDate,
                status: s.status,
                type: selectedAppointmentSetting?.name || "Clinic",
              }));

            setSlots(mappedSlots);
          } else {
            setSlots([]);
          }
        } catch (error) {
          console.error("Error fetching slots", error);
          toast.error("Failed to load slots");
        } finally {
          setLoadingSlots(false);
        }
      } else if (!selectedAppointmentSetting) {
        setSlots([]); // Clear slots if no clinic selected
      }
    };

    fetchSlots();
  }, [isExpanded, selectedAppointmentSetting, date, doctor]);

  const handleClinicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const selected = doctor?.appointmentSettings?.find(
      (setting) => setting.id === e.target.value,
    );
    // If "Select Clinic" (value "") is selected, selected will be undefined, which is fine
    // But strictly we might want to allow clearing it?
    // The user says "when user selects any clinic", usually implies picking one.
    // If they pick the default empty one, we should probably clear.
    if (selected) {
      setSelectedAppointmentSetting(selected);
    } else {
      setSelectedAppointmentSetting(null);
    }
  };

  return (
    <div
      className={`rounded-lg transition-all ${isSelected ? "bg-blue-300/20 border-blue-200 border" : "bg-white border hover:border-blue-200 shadow-sm"}`}
    >
      <div
        onClick={(e) => onDoctorClick(doctor?.doctorDetails || null, e)}
        className="cursor-pointer p-3"
      >
        {/* Doctor Info */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100">
            <img
              src={doctor?.doctorDetails.profileImage || placeholderAvatar}
              alt={doctor?.doctorDetails.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = placeholderAvatar;
              }}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-medium text-gray-900">
                {doctor?.doctorDetails.name}
              </h4>

              {/* Clinic Dropdown */}
              <div onClick={(e) => e.stopPropagation()}>
                {doctor?.appointmentSettings &&
                  doctor.appointmentSettings.length > 0 && (
                    <select
                      value={selectedAppointmentSetting?.id || ""}
                      onChange={handleClinicChange}
                      className="border border-gray-300 px-2 py-1 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[150px]"
                    >
                      <option value="">Select Clinic</option>
                      {doctor.appointmentSettings.map((setting) => (
                        <option key={setting.id} value={setting.id}>
                          {setting.name?.length > 25
                            ? setting.name?.substring(0, 25) + "..."
                            : setting.name || "Clinic"}
                        </option>
                      ))}
                    </select>
                  )}
              </div>
            </div>

            <p className="text-sm text-gray-500">
              {doctor?.doctorDetails.specialization.join(", ")} |{" "}
              {doctor?.doctorDetails.hospitalName}
            </p>

            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-gray-500">
                {/* Display fee of selected clinic */}
                {selectedAppointmentSetting
                  ? `₹${selectedAppointmentSetting.consultationFee}`
                  : ""}
              </span>
              <button
                className={`transition-colors p-0 m-0 w-7 h-7 rounded-full flex items-center justify-center border-none outline-none ${isExpanded ? "bg-red-800" : "bg-blue-600"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                <ChevronRight
                  className={`font-black w-5 h-5 text-white transition-transform ${isExpanded ? "rotate-90" : ""}`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="my-2 w-full border-t border-dashed border-gray-300 pb-4">
          {/* Doctor Stats */}
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {doctor?.doctorDetails.experience}
                </div>
                <div className="text-xs text-gray-500">Experience</div>
              </div>
              <div className="text-center border-x border-gray-100">
                <div className="text-sm font-medium text-gray-900">{"N/A"}</div>
                <div className="text-xs text-gray-500">Treated</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {selectedAppointmentSetting
                    ? `₹${selectedAppointmentSetting.consultationFee}`
                    : "N/A"}
                </div>
                <div className="text-xs text-gray-500">Fee</div>
              </div>
            </div>
          </div>

          {/* Time Slots */}
          <div onClick={(e) => e.stopPropagation()} className="p-4 space-y-3">
            <h5 className="text-sm font-medium text-gray-900">
              Schedules {date ? `(${date})` : ""}
            </h5>

            {!selectedAppointmentSetting ? (
              <p className="text-center text-sm text-gray-500 py-2">
                Please select a clinic to view available slots.
              </p>
            ) : loadingSlots ? (
              <div className="flex justify-center p-4">
                <Loader2 className="animate-spin text-emerald-600 w-6 h-6" />
              </div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {slots.map((schedule) => (
                  <button
                    key={schedule.id}
                    onClick={(e) =>
                      onTimeSlotClick(
                        e,
                        doctor?.doctorDetails || null,
                        schedule,
                      )
                    }
                    disabled={schedule.status !== "Available"}
                    className={`px-3 py-2 text-xs font-medium rounded-2xl transition-colors ${
                      selectedTimeSlot?.id === schedule.id
                        ? "bg-white border-emerald-700 border text-emerald-700 shadow-sm"
                        : schedule.status === "Available"
                          ? "text-gray-700 border-gray-400 border hover:bg-gray-50"
                          : "text-gray-400 border-gray-300 border bg-gray-100 cursor-not-allowed"
                    }`}
                  >
                    {schedule.start_time} - {schedule.end_time}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-500 py-2">
                No slots available for this clinic on this date.
              </p>
            )}
          </div>

          {/* Book Appointment Button */}
          {isSelected && onBookAppointment && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full p-4 pt-2 flex items-center justify-center"
            >
              <button
                onClick={onBookAppointment}
                disabled={!selectedTimeSlot}
                className="w-[80%] px-4 py-2.5 bg-emerald-600 text-white rounded-3xl hover:bg-emerald-700 transition-colors disabled:bg-gray-200 disabled:cursor-not-allowed"
              >
                {!selectedTimeSlot
                  ? "Select a time slot to book"
                  : "Book Appointment"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
